<template>
    <app-dialog
        v-model="isOpen"
        size="lg"
        fixed-width
        append-to-body
        @close="onClose"
    >
        <template #header>
            <h4 class="text-lg font-semibold text-theme-base">
                {{ editing ? "Editar tabla" : "Nueva tabla" }}
            </h4>
            <p class="mt-1 text-sm text-theme-muted">
                Llena las celdas como en una hoja de cálculo. La primera fila es
                el encabezado.
            </p>
        </template>

        <div class="overflow-x-auto">
            <table class="w-full border-collapse">
                <thead>
                    <tr>
                        <th
                            v-for="(alignment, columnIndex) in alignments"
                            :key="columnIndex"
                            class="p-1 align-bottom"
                        >
                            <div
                                class="flex items-center justify-between gap-2"
                            >
                                <alignment-picker
                                    :value="alignment"
                                    @input="setAlignment(columnIndex, $event)"
                                />
                                <button
                                    v-if="alignments.length > 1"
                                    type="button"
                                    title="Quitar columna"
                                    aria-label="Quitar columna"
                                    class="rounded p-1 text-theme-muted hover:bg-theme-hover hover:text-theme-danger"
                                    @click="removeColumn(columnIndex)"
                                >
                                    <x-mark-icon class="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </th>
                        <th class="w-8 p-1"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
                        <td
                            v-for="(cell, columnIndex) in row"
                            :key="columnIndex"
                            class="p-1"
                        >
                            <input
                                type="text"
                                :value="cell"
                                :placeholder="
                                    rowIndex === 0 ? 'Encabezado' : 'Dato'
                                "
                                class="w-full min-w-32 rounded-md border border-theme-border bg-theme-input px-2 py-1.5 text-sm text-theme-base focus:outline-none focus:ring-1 focus:ring-theme-primary"
                                :class="rowIndex === 0 ? 'font-semibold' : ''"
                                @input="
                                    setCell(
                                        rowIndex,
                                        columnIndex,
                                        $event.target.value
                                    )
                                "
                            />
                        </td>
                        <td class="p-1">
                            <button
                                v-if="canRemoveRow(rowIndex)"
                                type="button"
                                title="Quitar fila"
                                aria-label="Quitar fila"
                                class="rounded p-1 text-theme-muted hover:bg-theme-hover hover:text-theme-danger"
                                @click="removeRow(rowIndex)"
                            >
                                <x-mark-icon class="w-3.5 h-3.5" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
            <app-button type="button" size="small" relief @click="addRow">
                Agregar fila
            </app-button>
            <app-button type="button" size="small" relief @click="addColumn">
                Agregar columna
            </app-button>
        </div>

        <p v-if="error" class="mt-2 text-xs text-theme-danger">{{ error }}</p>

        <template #footer>
            <div class="flex justify-end">
                <app-button type="button" @click="submit">
                    {{ editing ? "Guardar tabla" : "Insertar tabla" }}
                </app-button>
            </div>
        </template>
    </app-dialog>
</template>

<script>
import XMarkIcon from "@/components/icons/XMarkIcon.vue";
import AppButton from "@/components/utils/AppButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

import AlignmentPicker from "./AlignmentPicker.vue";
import { createEmptyTable, isEmptyTable } from "./tables";

const MINIMUM_ROWS = 2;

export default {
    name: "TableEditorDialog",
    components: { AlignmentPicker, AppButton, AppDialog, XMarkIcon },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            default: () => false,
            type: Boolean,
            required: true
        },
        table: {
            default: () => null,
            type: Object,
            required: false
        },
        editing: {
            default: () => false,
            type: Boolean,
            required: false
        }
    },
    emits: ["input", "submit"],
    data: () => ({
        alignments: [],
        rows: [],
        error: ""
    }),
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.loadTable();
        }
    },
    created() {
        this.loadTable();
    },
    methods: {
        loadTable() {
            const source =
                this.table && this.table.rows && this.table.rows.length
                    ? this.table
                    : createEmptyTable();
            const columnCount = source.rows[0] ? source.rows[0].length : 1;
            this.rows = source.rows.map((row) => [...row]);
            this.alignments = Array.from(
                { length: columnCount },
                (item, index) => (source.alignments || [])[index] || "left"
            );
            this.error = "";
        },
        setCell(rowIndex, columnIndex, value) {
            this.$set(this.rows[rowIndex], columnIndex, value);
        },
        setAlignment(columnIndex, value) {
            this.$set(this.alignments, columnIndex, value);
        },
        addRow() {
            this.rows.push(this.alignments.map(() => ""));
        },
        canRemoveRow(rowIndex) {
            return rowIndex > 0 && this.rows.length > MINIMUM_ROWS;
        },
        removeRow(rowIndex) {
            if (!this.canRemoveRow(rowIndex)) return;
            this.rows.splice(rowIndex, 1);
        },
        addColumn() {
            this.rows.forEach((row) => row.push(""));
            this.alignments.push("left");
        },
        removeColumn(columnIndex) {
            if (this.alignments.length <= 1) return;
            this.rows.forEach((row) => row.splice(columnIndex, 1));
            this.alignments.splice(columnIndex, 1);
        },
        submit() {
            const table = {
                alignments: [...this.alignments],
                rows: this.rows.map((row) => [...row])
            };
            if (isEmptyTable(table)) {
                this.error = "Escribe al menos un dato en la tabla";
                return;
            }
            this.error = "";
            this.$emit("submit", table);
        },
        onClose() {
            this.error = "";
        }
    }
};
</script>
